import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { toast } from "sonner";

export default function TeamMemberships() {
  const [email, setEmail] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const queryClient = useQueryClient();

  const {
    data: userTeams,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminUserTeams", searchedEmail],
    queryFn: () => AdminApi.getUserTeams(searchedEmail),
    enabled: searchedEmail.length > 0,
    retry: false,
  });

  const joinMutation = useMutation({
    mutationFn: (teamId: number) => AdminApi.joinTeam(teamId),
    onSuccess: () => {
      toast.success("Joined team successfully");
      queryClient.invalidateQueries({
        queryKey: ["adminUserTeams", searchedEmail],
      });
    },
    onError: (err) => {
      toast.error(`Failed to join team: ${err.message}`);
    },
  });

  const leaveMutation = useMutation({
    mutationFn: (teamId: number) => AdminApi.leaveTeam(teamId),
    onSuccess: () => {
      toast.success("Left team successfully");
      queryClient.invalidateQueries({
        queryKey: ["adminUserTeams", searchedEmail],
      });
    },
    onError: (err) => {
      toast.error(`Failed to leave team: ${err.message}`);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSearchedEmail(email.trim());
    }
  };

  const isMutating = joinMutation.isPending || leaveMutation.isPending;

  return (
    <AdminLayout
      title="Join or Leave Teams"
      description="Search for a user by email and join or leave their teams"
      breadcrumb="Join or Leave Teams"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="user@example.com"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Searching..." : "Search"}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              {error instanceof Error ? error.message : "User not found"}
            </p>
          </div>
        )}

        {userTeams && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                Teams for:{" "}
                <span className="font-medium">{userTeams.email}</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {userTeams.teams.length} team
                {userTeams.teams.length !== 1 ? "s" : ""} found
              </p>
            </div>

            {userTeams.teams.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                This user has no teams.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {userTeams.teams.map((team) => (
                  <div
                    key={team.teamId}
                    className="px-6 py-4 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {team.teamName || "(unnamed)"}
                      </p>
                      <p className="text-sm text-gray-500">
                        ID: {team.teamId} &middot; {team.teamExternalId}
                      </p>
                    </div>
                    <div>
                      {team.adminIsMember ? (
                        <button
                          onClick={() => leaveMutation.mutate(team.teamId)}
                          disabled={isMutating}
                          className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Leave
                        </button>
                      ) : (
                        <button
                          onClick={() => joinMutation.mutate(team.teamId)}
                          disabled={isMutating}
                          className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Join
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
